import { FuelIcon, FuelTextField } from '@frete.com/fuel-react';
import {
  BACKSPACE_KEY,
  INVALID_MESSAGE,
  NUMBER_REGEX,
  SEARCH_TERM_LIMIT,
} from 'components/TruckerList/Sidebar/constants';
import { SUGGESTED_TAB } from 'context/reducers/tabs.type';
import { TTruckerItem } from 'context/reducers/truckers.type';
import { useAppContext } from 'hooks/useAppContext';
import { useFilteredData } from 'hooks/useFilteredData';
import { useSearchTrucker } from 'hooks/useSearchTrucker';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearMask, cpfMask, phoneMask } from 'utils/masks';
import * as Styled from './styled';

const SearchTrucker: FC = () => {
  const {
    dispatch,
    state: {
      searchTruckers: { dataSearch, term: searchedTerm, isSearchFieldOnFocus },
      tabs: { activeTab },
      deviceSize: { isDesktop },
    },
  } = useAppContext();

  const navigate = useNavigate();
  const { searchTrucker } = useSearchTrucker();
  const { filteredDataSuggested } = useFilteredData();

  const [searchError, setSearchError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const searchInput = useRef(null);

  const setActiveDriver = (trucker: TTruckerItem) => {
    dispatch({
      type: 'truckers_driver_active_set',
      payload: { activeDriver: { ...trucker, id: trucker.person.id } },
    });
    navigate(trucker.person.id);
  };

  const reOpenProfileOnSearchClear = () => {
    if (activeTab === SUGGESTED_TAB && !!filteredDataSuggested.length) {
      setActiveDriver(filteredDataSuggested[0]);
      return;
    }

    navigate('', { replace: true });
  };

  const getTermFromEvent = (event: KeyboardEvent | ClipboardEvent): string => {
    const target = event.target as HTMLInputElement;

    const { value: term } = target;

    return term;
  };

  const removeTrailingWhitespaces = (term: string | undefined): string | undefined => (!term ? term : term.trim());

  const isValidCharacter = (key: string): boolean => !!(key.match(NUMBER_REGEX) || key === BACKSPACE_KEY);

  const hasNonNumeric = (term: string): boolean => !NUMBER_REGEX.test(term) && term !== '';

  const handleKeyUp = (event: KeyboardEvent): void => {
    event.stopPropagation();

    let inputSearchValue = getTermFromEvent(event);

    inputSearchValue = removeTrailingWhitespaces(inputSearchValue);

    if (inputSearchValue === searchTerm) return;

    const clearedTerm = clearMask(inputSearchValue);

    const hasError = hasNonNumeric(clearedTerm) || clearedTerm.length > SEARCH_TERM_LIMIT;

    setSearchError(hasError);

    setSearchTerm(clearedTerm);

    if (hasError) return;

    dispatch({
      type: 'search_truckers_set_term',
      payload: clearedTerm,
    });

    /* istanbul ignore else */
    if (clearedTerm.length === SEARCH_TERM_LIMIT) {
      searchTrucker(clearedTerm);
    }
  };

  const handleKeyPress = (event: KeyboardEvent): void => {
    event.stopPropagation();

    const term = getTermFromEvent(event);

    const { key } = event;

    if (!isValidCharacter(key) || clearMask(term).length >= SEARCH_TERM_LIMIT) {
      event.preventDefault();
    }
  };

  const handleOnFocus = (): void => {
    dispatch({ type: 'search_field_focus_set' });
  };

  const handleOnBlur = (): void => {
    /* istanbul ignore else */
    if (searchTerm.length === 0) {
      dispatch({
        type: 'search_truckers_clear',
      });
    }
  };

  const onFuelClear = (): void => {
    setSearchError(false);
    setSearchTerm('');
    dispatch({
      type: 'search_truckers_clear',
    });

    if (isDesktop) reOpenProfileOnSearchClear();
  };

  useEffect(() => {
    if (!searchTerm && !!searchedTerm) {
      setSearchTerm(searchedTerm);
    }
  }, []);

  useEffect(() => {
    if (!isSearchFieldOnFocus) setSearchTerm('');
  }, [isSearchFieldOnFocus]);

  useEffect(() => {
    if (searchTerm.length === SEARCH_TERM_LIMIT && !!dataSearch) {
      if (dataSearch.wasSearchedByCpf) {
        setSearchTerm(cpfMask(searchTerm));
      } else {
        setSearchTerm(phoneMask(searchTerm));
      }
    }
  }, [dataSearch, searchTerm]);

  return (
    <Styled.FiltersContainer searchError={searchError} searchTerm={searchTerm}>
      <FuelTextField
        variant={!isDesktop ? 'medium' : 'large'}
        ref={searchInput}
        value={searchTerm}
        onKeyUp={handleKeyUp}
        onKeyPress={handleKeyPress}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        placeholder="Consulte por CPF ou telefone"
        type="text"
        hasSuffix
        state={searchError ? 'error' : 'default'}
        inputmode="numeric"
        data-testid="search-bar"
      >
        {searchTerm && (
          <FuelIcon
            name="close"
            iconStyle="outlined"
            slot="suffix"
            data-testid="clear-search-field"
            onClick={() => onFuelClear()}
          />
        )}

        <FuelIcon slot="suffix" name="search" size="tiny" />
      </FuelTextField>
      <span>{INVALID_MESSAGE}</span>
    </Styled.FiltersContainer>
  );
};

export default SearchTrucker;
